/**
 * Data Source CRUD Service
 * Handles create, read, update, delete operations for data sources.
 */
import { logger } from '@/utils/logger'
import { httpGet, httpPost, httpDelete, getBaseUrl } from '@/services/http-client'
import { unwrap, parseOrThrow } from '@/services/api-error'
import { type ApiResult } from '@/types/api'
import { CDMSourceListSchema, type CDMSource } from '@/models/webapi.types'
import type {
  DataSource,
  SourceRequest,
  SourceDetails,
  DaimonRequest,
} from '@/models/datasource.types'

/**
 * Get list of available CDM data sources
 * Endpoint: GET /source/sources
 */
export async function fetchCDMSources(): Promise<ApiResult<CDMSource[]>> {
  return unwrap(async () => {
    const data = await httpGet<unknown>('/source/sources')
    return parseOrThrow(CDMSourceListSchema, data, 'Invalid source list response')
  }, 'SourceService')
}

/**
 * Get connection-level details (JDBC URL, credentials) for a source.
 * WebAPI serves these only from the id-keyed admin endpoint; `/source/{key}` returns just the public metadata.
 */
export async function getSourceDetails(sourceId: number): Promise<SourceDetails> {
  try {
    logger.debug('SourceService', `Fetching source details for id ${sourceId}`)
    const response = await httpGet<SourceDetails>(`/source/details/${sourceId}`)
    logger.debug('SourceService', `Successfully fetched source details for id ${sourceId}`)
    return response
  } catch (error) {
    logger.error('SourceService', 'Failed to fetch source details', { sourceId, error })
    throw new Error('Unable to load source details. Please try again.')
  }
}

/**
 * Create a new data source
 */
export async function createSource(request: SourceRequest, keyfile?: File): Promise<DataSource> {
  try {
    logger.debug('SourceService', 'Creating new source', { name: request.name })

    const response = await uploadSource('/source', 'POST', request, keyfile)

    logger.debug('SourceService', `Successfully created source: ${response.sourceName}`)
    return response
  } catch (error) {
    logger.error('SourceService', 'Failed to create source', { name: request.name, error })
    throw new Error('Unable to create data source. Please try again.')
  }
}

/**
 * Update an existing data source
 *
 * WebAPI's update endpoint is keyed on the numeric `sourceId`
 * (`PUT /source/{sourceId}`), not the string `sourceKey`.
 */
export async function updateSource(
  sourceId: number,
  request: SourceRequest,
  keyfile?: File
): Promise<DataSource> {
  try {
    logger.debug('SourceService', `Updating source id ${sourceId}`, { name: request.name })

    const response = await uploadSource(`/source/${sourceId}`, 'PUT', request, keyfile)

    logger.debug('SourceService', `Successfully updated source: ${response.sourceName}`)
    return response
  } catch (error) {
    logger.error('SourceService', 'Failed to update source', {
      sourceId,
      name: request.name,
      error,
    })
    throw new Error('Unable to update data source. Please try again.')
  }
}

/**
 * Delete a data source
 *
 * WebAPI's delete endpoint is keyed on the numeric `sourceId`
 * (`DELETE /source/{sourceId}`), not the string `sourceKey` — sending the
 * key resolves to a non-existent route and the delete fails.
 */
export async function deleteSource(sourceId: number): Promise<void> {
  try {
    logger.debug('SourceService', `Deleting source id ${sourceId}`)
    await httpDelete(`/source/${sourceId}`)
    logger.debug('SourceService', `Successfully deleted source id ${sourceId}`)
  } catch (error) {
    logger.error('SourceService', 'Failed to delete source', { sourceId, error })
    throw new Error('Unable to delete data source. Please try again.')
  }
}

/**
 * Test connection to a data source
 */
export async function testConnection(sourceKey: string): Promise<boolean> {
  try {
    logger.debug('SourceService', `Testing connection for source: ${sourceKey}`)
    await httpGet(`/source/${sourceKey}/connectionCheck`)
    logger.debug('SourceService', `Connection test successful for source: ${sourceKey}`)
    return true
  } catch (error) {
    logger.error('SourceService', 'Connection test failed', { sourceKey, error })
    return false
  }
}

/**
 * Refresh source cache
 */
export async function refreshSourceCache(sourceKey: string): Promise<void> {
  try {
    logger.debug('SourceService', `Refreshing cache for source: ${sourceKey}`)
    await httpPost(`/source/${sourceKey}/refreshSourceCache`)
    logger.debug('SourceService', `Cache refresh started for source: ${sourceKey}`)
  } catch (error) {
    logger.error('SourceService', 'Failed to refresh source cache', { sourceKey, error })
    throw new Error('Unable to refresh source cache. Please try again.')
  }
}

/**
 * Map SourceRequest to WebAPI's SourceRequest JSON contract.
 */
function mapRequestToApiPayload(request: SourceRequest): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: request.name,
    dialect: request.dialect,
    key: request.key,
    connectionString: request.connectionString,
  }

  if (request.username) {
    payload.username = request.username
  }

  if (request.password) {
    payload.password = request.password
  }

  // Always sent, even for a dialect that has nothing to do with Kerberos.
  // WebAPI converts the request with
  // `KerberosAuthMechanism.getByName(request.getKrbAuthMethod())`, and that
  // helper calls `name.toUpperCase()` before matching, so omitting the field
  // fails the whole create/update with a NullPointerException surfaced as an
  // opaque HTTP 500 ConversionFailedException. `DEFAULT` is the enum's own
  // fallback for an unrecognised name, so it is what a source with no Kerberos
  // mechanism should record anyway.
  payload.krbAuthMethod = request.krbAuthMethod ?? 'DEFAULT'

  if (request.krbAdminServer) {
    payload.krbAdminServer = request.krbAdminServer
  }

  if (request.daimons && request.daimons.length > 0) {
    payload.daimons = request.daimons.map((daimon: DaimonRequest, index: number) => ({
      daimonType: daimon.daimonType,
      tableQualifier: daimon.tableQualifier,
      priority: daimon.priority ?? index,
    }))
  }

  return payload
}

/**
 * Save a source using multipart/form-data
 *
 * WebAPI's create/update endpoints consume multipart/form-data only, and
 * resolve the `source` part through its message converters — so the part has
 * to carry an `application/json` content type. A plain JSON request body, or
 * an untyped part, is rejected with HttpMediaTypeNotSupportedException.
 */
async function uploadSource(
  endpoint: string,
  method: 'POST' | 'PUT',
  request: SourceRequest,
  keyfile?: File
): Promise<DataSource> {
  const formData = new FormData()

  // Add source data as JSON blob
  const sourceData = mapRequestToApiPayload(request)
  formData.append('source', new Blob([JSON.stringify(sourceData)], { type: 'application/json' }))

  if (keyfile) {
    formData.append('keyfile', keyfile)
  }

  // Get auth token
  const { useAuthStore } = await import('@/stores/auth')
  const token = useAuthStore().token

  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const url = `${getBaseUrl()}${endpoint}`
  const response = await fetch(url, {
    method,
    headers,
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error')
    throw new Error(`HTTP ${response.status}: ${errorText}`)
  }

  return response.json()
}
